/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import {
  deleteQualitySpecificationApi,
  getAllQualitySpecificationsApi,
  qualitySpecificationsListApi,
  updateQualitySpecificationsStatusApi,
} from "../../services/_qualitySpecifications";
import { Link } from "react-router-dom";
import moment from "moment";
import Pagination from "../../components/Pagination";
import { getPermissions } from "../../context/AuthContext";
import ViewQualitySpecification from "./ViewQualitySpecifications";
import Spinner from "../../theme/Spinner";
import { Autocomplete, TextField } from "@mui/material";
import { getProductNameListApi } from "../../services/_product";
import ReactSelect from "react-select";
import ReCAPTCHA from "react-google-recaptcha";
import { errorToast, successToast } from "../../components/Toast";

const QualitySpecificationsList = () => {
  const permissions = getPermissions();
  const [records, setRecords] = useState([]);
  const [productList, setProductList] = useState([]);
  const [page, setPage] = useState<number>(1);
  const [perPage] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [applyFilter, setApplyFilter] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [filterName, setFilterName] = useState<string>("");
  const [filterProductId, setFilterProductId] = useState<string>("");
  const [qualitySpecificationId, setQualitySpecificationId] = useState("");
  const [displayFilter, setDisplayFilter] = useState(false);
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [filteredNameSuggestions, setFilteredNameSuggestions] = useState([]);
  const [sortKey, setSortKey] = useState<string>("");
  const [sortType, setSortType] = useState<string>("");
  const productRef = useRef<any>(null);
  const recaptchaRef = useRef<any>(null);
  const [selectedItemId, setSelectedItemId] = useState<string | null>(null);
  const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);

  const toggleFilter = async () => {
    setDisplayFilter(!displayFilter);
  };

  const getQualitySpecificationsList = async (
    page: number,
    perPage: number,
    isFilter?: boolean,
    sortKey?: string,
    sortType?: string
  ) => {
    setLoading(true);
    let query = `page=${page}&per_page=${perPage}`;
    if (isFilter) {
      if (filterName) {
        query += `&name=${filterName}`;
      }
      if (filterProductId) {
        query += `&product_id=${filterProductId}`;
      }
      if (sortKey && sortType) {
        query += `&sort_key=${sortKey}&sort_type=${sortType}`;
      }
    }
    const result: any = await qualitySpecificationsListApi(query);
    if (result.isSuccess) {
      const totalPagesCount = Math.ceil(result.data.total_records / perPage);
      setTotalRecords(result.data.total_records);
      setTotalPages(totalPagesCount);
      setRecords(result.data.records);
    }
    setLoading(false);
  };

  const handleAutocompleteFilter = (
    filterType: "name",
    newValue: string | null
  ) => {
    const filterData = userSuggestions.filter((suggestion: any) =>
      suggestion[filterType]
        ?.toLowerCase()
        .includes((newValue || "").toLowerCase())
    );
    if (filterType === "name") {
      setFilterName(newValue || "");
      setFilteredNameSuggestions(filterData);
    }
  };

  useEffect(() => {
    const fetchNameSuggestions = async () => {
      const result: any = await getAllQualitySpecificationsApi();
      if (result.isSuccess) {
        setFilteredNameSuggestions(result?.data || []);
        setUserSuggestions(result?.data || []);
      }
    };
    const fetchProductNameSuggestions = async () => {
      const result: any = await getProductNameListApi();
      if (result.isSuccess) {
        setProductList(result?.data || []);
      }
    };
    fetchNameSuggestions();
    fetchProductNameSuggestions();
  }, []);

  const filterList = async () => {
    setApplyFilter(true);
    setFilterName(filterName);
    await getQualitySpecificationsList(page, perPage, true);
  };

  const resetList = async () => {
    setPage(1);
    setApplyFilter(false);
    setFilterName("");
    setFilterProductId("");
    if (productRef.current) {
      productRef.current.clearValue();
    }
    setFilteredNameSuggestions(userSuggestions);
    await getQualitySpecificationsList(page, perPage);
  };

  const prevButton = async () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };

  const nextButton = async () => {
    if (totalPages > page) {
      setPage(page + 1);
    }
  };

  const updateQualitySpecificationsStatus = async (
    id: string,
    status: boolean
  ) => {
    setLoading(true);
    await updateQualitySpecificationsStatusApi({
      id: id,
      status: !status,
    });
    setQualitySpecificationId("");
    await getQualitySpecificationsList(page, perPage, true);
    setLoading(false);
  };

  const sortTable = async (key: string) => {
    let type = sortType === "asc" ? "desc" : "asc";
    if (sortKey !== key) {
      type = "asc";
    }
    setSortKey(key);
    setSortType(type);
    await getQualitySpecificationsList(page, perPage, true, key, type);
  };

  const confirmDelete = async () => {
    if (selectedItemId) {
      const result = await deleteQualitySpecificationApi(selectedItemId);
      if (result.isSuccess) {
        await getQualitySpecificationsList(page, perPage, true);
        successToast(result.message);
        setUserSuggestions((prev) => {
          const filtered = prev.filter((q: any) => q.id !== selectedItemId);
          setFilteredNameSuggestions(filtered);
          return filtered;
        });
      } else {
        errorToast(result.message);
      }
      setSelectedItemId(null);
    }
    resetReCaptcha();
  };

  const handleDeleteClick = (id: any) => {
    if (isCaptchaVerified) {
      resetReCaptcha();
    }
    setSelectedItemId(id);
  };
  const resetReCaptcha = () => {
    recaptchaRef.current?.reset();
    setIsCaptchaVerified(false);
  };

  useEffect(() => {
    if (page && perPage) {
      getQualitySpecificationsList(page, perPage, applyFilter);
    }
  }, [page, perPage, applyFilter]);

  return (
    <>
      <div className="card">
        <div className="card-header pb-0 d-flex justify-content-between">
          <div>
            <h5>Quality Specification List</h5>
          </div>

          <div>
            <Link
              to="#"
              type="button"
              className="btn btn-label-github"
              style={{ marginRight: "15px" }}
              onClick={toggleFilter}
            >
              <span>
                <i className="ti ti-filter me-sm-1"></i>{" "}
                <span className="d-none d-sm-inline-block">Filter</span>
              </span>
            </Link>
            {permissions.includes("create-quality-specification") && (
              <Link
                className="btn btn-primary waves-effect waves-light"
                type="button"
                to="/create-quality-specification"
              >
                <span>
                  <i className="ti ti-plus me-sm-1"></i>{" "}
                  <span className="d-none d-sm-inline-block">
                    Add Quality Specification
                  </span>
                </span>
              </Link>
            )}
          </div>
        </div>

        {displayFilter && (
          <div className="card-body">
            <form className="dt_adv_search">
              <div className="row">
                <div className="col-12">
                  <div className="row g-3">
                    <div className="col-12 col-sm-6 col-lg-3">
                      <label
                        className="form-label"
                        style={{ fontSize: "0.9375rem" }}
                      >
                        Name:
                      </label>
                      <Autocomplete
                        disablePortal
                        options={filteredNameSuggestions.map(
                          (suggestion: any) => suggestion.name
                        )}
                        onChange={(event, newValue) =>
                          handleAutocompleteFilter("name", newValue)
                        }
                        value={filterName}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            className="form-control"
                            type="text"
                            placeholder="Enter Name"
                            value={filterName}
                            onChange={(e) => setFilterName(e.target.value)}
                          />
                        )}
                      />
                    </div>
                    <div className="col-12 col-sm-6 col-lg-3">
                      <label
                        className="form-label"
                        style={{ fontSize: "0.9375rem" }}
                      >
                        Product:
                      </label>
                      {(() => {
                        const selectedOption = productList?.find(
                          (option: any) => option.id === filterProductId
                        );
                        const formatOptionLabel = (
                          option: any,
                          { context }: any
                        ) => {
                          if (context === "value") {
                            return option.name;
                          } else {
                            return (
                              <div>
                                <label className="form-label">
                                  {option.name}
                                </label>
                                <br />
                                <span className="form-label">
                                  {option.description}
                                </span>
                              </div>
                            );
                          }
                        };
                        return (
                          <ReactSelect
                            ref={productRef}
                            classNamePrefix="react-select"
                            placeholder="Select Product"
                            options={productList}
                            getOptionLabel={(option: any) => option.name}
                            formatOptionLabel={formatOptionLabel}
                            getOptionValue={(option: any) => option.id}
                            onChange={(selectedOption: any) => {
                              setFilterProductId(selectedOption?.id || "");
                            }}
                            value={selectedOption}
                            isClearable
                            isSearchable
                          />
                        );
                      })()}
                    </div>
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={filterList}
                      >
                        Filter
                      </button>
                      <button
                        type="button"
                        className="btn btn-label-github ms-5"
                        onClick={resetList}
                      >
                        Reset
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        <div className="card-datatable table-responsive">
          {loading && <Spinner isFullScreen={false} />}
          <table className="dt-advanced-search table table-nowrap">
            <thead>
              <tr>
                <th>#</th>
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("name")}
                >
                  Name
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("product")}
                >
                  Product
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("description")}
                >
                  Description
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                {permissions.includes("update-quality-specification") && (
                  <th
                    className="cursor-pointer"
                    onClick={() => sortTable("status")}
                  >
                    Status
                    <span>
                      <i className="ti ti-arrows-down-up sorting-icon"></i>
                    </span>
                  </th>
                )}
                <th
                  className="cursor-pointer"
                  onClick={() => sortTable("created_at")}
                >
                  Created At
                  <span>
                    <i className="ti ti-arrows-down-up sorting-icon"></i>
                  </span>
                </th>
                {(permissions.includes("update-quality-specification") ||
                  permissions.includes("delete-quality-specification")) && (
                  <th>Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {records.length > 0 ? (
                records.map((item: any, i: number) => (
                  <tr key={item.id}>
                    <td>{(page - 1) * perPage + 1 + i}</td>
                    <td>
                      <Link
                        to={"#"}
                        onClick={() => {
                          setQualitySpecificationId(item.id);
                        }}
                        title="View Quality Specification Details"
                        data-bs-toggle="modal"
                        data-bs-target="#viewQualitySpecification"
                      >
                        {item.name}
                      </Link>
                    </td>
                    <td>{item.product?.name}</td>
                    <td>{item.description}</td>
                    {permissions.includes("update-quality-specification") && (
                      <td>
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="status"
                            defaultChecked={item.status ? true : false}
                            onClick={() => {
                              updateQualitySpecificationsStatus(
                                item.id,
                                item.status
                              );
                            }}
                          />
                        </div>
                      </td>
                    )}
                    <td>
                      {moment(item.created_at).format("YYYY-MM-DD HH:mm:ss")}
                    </td>
                    {(permissions.includes("update-quality-specification") ||
                      permissions.includes("delete-quality-specification")) && (
                      <td>
                        {permissions.includes(
                          "update-quality-specification"
                        ) && (
                          <Link
                            to={`/edit-quality-specification/${item.id}`}
                            className="btn btn-sm btn-text-secondary rounded-pill btn-icon"
                            title="Edit QualitySpecifications"
                          >
                            <i className="ti ti-pencil ti-md"></i>
                          </Link>
                        )}

                        {permissions.includes(
                          "delete-quality-specification"
                        ) && (
                          <Link
                            to={`#`}
                            className="btn btn-sm btn-text-secondary rounded-pill btn-icon"
                            data-bs-toggle="modal"
                            data-bs-target="#deleteQualitySpecificationModal"
                            onClick={() => handleDeleteClick(item.id)}
                          >
                            <i className="ti ti-trash ti-md"></i>
                          </Link>
                        )}
                      </td>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={
                      permissions.includes("update-quality-specification") ||
                      permissions.includes("delete-quality-specification")
                        ? 6
                        : 4
                    }
                    className="text-center"
                  >
                    No records found
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          <div
            className="modal fade"
            id="deleteQualitySpecificationModal"
            tabIndex={-1}
            aria-hidden="true"
          >
            <div className="modal-dialog modal-simple modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-body text-center">
                  <i
                    className="fa-solid fa-circle-exclamation"
                    style={{
                      fontSize: "48px",
                      color: "#ff4c51",
                      marginBottom: "20px",
                      overflow: "hidden",
                    }}
                  ></i>
                  <p style={{ fontSize: "30px" }}>Are you sure?</p>
                  <br />
                  <p style={{ fontSize: "20px" }}>
                    Do you want to delete this <b>Quality Specification</b>?
                    <br />
                    This action can't be undone!
                  </p>
                  <div
                    style={{
                      marginTop: "10px",
                      marginLeft: "50px",
                      marginBottom: "10px",
                      height: "80px",
                      width: "305px",
                    }}
                  >
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      sitekey={
                        process.env.REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY || ""
                      }
                      onChange={(value: any) => {
                        if (value) {
                          setIsCaptchaVerified(true);
                        }
                      }}
                    />
                  </div>
                  <div className="text-center">
                    <button
                      type="button"
                      className="btn btn-danger me-3"
                      onClick={() => {
                        confirmDelete();
                        resetList();
                      }}
                      disabled={!isCaptchaVerified}
                      data-bs-dismiss="modal"
                    >
                      Yes, Delete
                    </button>
                    <button
                      type="button"
                      className="btn btn-label-secondary"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Pagination
          page={page}
          perPage={perPage}
          totalRecords={totalRecords}
          totalPages={totalPages}
          records={records}
          prevButton={prevButton}
          nextButton={nextButton}
        />
        <ViewQualitySpecification
          qualitySpecificationId={qualitySpecificationId}
        />
      </div>
    </>
  );
};

export default QualitySpecificationsList;
